import { useState, useEffect, useRef, useContext } from "react";
import clsx from "clsx";

import { Chat } from "@assets/icons";
import { useChatList, useGlobalChatList } from "@hooks/chats";
import messageSound from "../../../assets/sounds/Message.mp3";
import { ChatContext } from "@contexts/Messages";
import { GlobalChatContext } from "@contexts/GlobalMessages";
import { UserContext } from "@contexts/User";

const ChatIcon = ({ isActiveTab, className, ...props }) => {
  const { isAllAccountsLogin } = useContext(UserContext);
  const isMounted = useRef(false);
  const soundRef = useRef(new Audio(messageSound));
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(null);
  // TODO(perf): this duplicates the chatRooms fetch that Messages.jsx also makes on its
  // own useChatList() call, with no shared cache between them (navbar + page both fetch
  // independently, every page load). A fix was attempted by moving useChatList's state
  // + socket listeners into ChatContext (src/contexts/Messages.jsx) so every consumer
  // shares one fetch - reverted because it introduced a permanent-loading-spinner bug:
  // the shared fetchChatList's `isUnmounted` guard got stuck `true` after the first time
  // `currentProfile`'s reference changed (which happens during normal login), silently
  // blocking every later setIsLoading(false)/setChatList(...) call. Any future attempt at
  // this needs a real fix for that guard (e.g. drop it, or reset it per fetch rather than
  // treating "effect re-ran" as "component unmounted"), not just reinstating the old code.
  const [chatList, { isLoading }] = useChatList({
    isPopulateChatParticipants: true,
    isPopulateLastMessage: true,
    isPopulateChatParticipantProfiles: false,
    allowReadMessageEventEmitting: false,
    isCacheEnabled: false,
  });

  const [globalChatList, { isLoading: isGlobalLoading }] = useGlobalChatList({
    isPopulateChatParticipants: true,
    isPopulateLastMessage: true,
    isPopulateChatParticipantProfiles: false,
    allowReadMessageEventEmitting: true,
    isCacheEnabled: true,
  });
  const { socket, sharedChatList } = useContext(ChatContext);
  const { socketForGlobalMessage, sharedGlobalChatList } =
    useContext(GlobalChatContext);

  // Prefer the live list a currently-mounted Messages page already fetched
  // (kept in sync via its own socket listeners) over this component's own
  // independent fetch below, so the two don't drift out of sync. Falls
  // back to the local fetch when no Messages page is mounted to publish one.
  const effectiveChatList = sharedChatList ?? chatList;
  const effectiveGlobalChatList = sharedGlobalChatList ?? globalChatList;

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    const globalChatListResult =
      !isGlobalLoading &&
      effectiveGlobalChatList
        .map((chatRoom) => chatRoom.unreadMessageCount)
        .reduce((prev, cur) => prev + cur, 0);
    const chatListResult =
      !isLoading &&
      effectiveChatList
        .map((chatRoom) => chatRoom.unreadMessageCount)
        .reduce((prev, cur) => prev + cur, 0);

    if (
      unreadMessagesCount !== null &&
      globalChatListResult + chatListResult > unreadMessagesCount
    ) {
      soundRef.current?.play().catch(() => {});
    }

    if (isAllAccountsLogin) setUnreadMessagesCount(chatListResult);
    else setUnreadMessagesCount(globalChatListResult + chatListResult);
  }, [effectiveChatList, effectiveGlobalChatList, socket, socketForGlobalMessage]);

  return (
    <div className="relative">
      {unreadMessagesCount && unreadMessagesCount > 0 ? (
        <div
          className={clsx(
            "absolute -right-2/5 -top-35% h-6 w-6 bg-primary rounded-full flex items-center justify-center border-1.5",
            isActiveTab ? "border-pampas" : "border-main",
          )}
        >
          <span className="text-white font-light-poppins font-black text-sm leading-5">
            {unreadMessagesCount}
          </span>
        </div>
      ) : null}
      <Chat className={className} {...props} />
    </div>
  );
};

export default ChatIcon;
